import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CheckCircle, ShoppingBag, Instagram, Youtube } from 'lucide-react';

const ThankYou = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Clear cart after successful purchase
    localStorage.removeItem('prsvr_cart_id');
    localStorage.removeItem('prsvr_cart_url');
  }, []);

  return (
    <div className="min-h-screen bg-charcoal">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-charcoal/95 backdrop-blur-md border-b border-warmSage/20">
        <nav className="container mx-auto px-6 py-4">
          <a href="/" className="flex items-center space-x-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/bcsdxd12_IMG_2797.PNG" 
              alt="PRSVR Logo" 
              className="h-10"
              style={{ filter: 'brightness(0) saturate(100%) invert(98%) sepia(6%) saturate(214%) hue-rotate(327deg) brightness(102%) contrast(96%)' }}
            />
          </a>
        </nav>
      </header>

      {/* Main Content */}
      <div className="pt-24 pb-16 px-6">
        <div className="container mx-auto max-w-3xl">
          {/* Success Icon */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-green-500/20 mb-6">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            
            <h1 className="text-4xl md:text-5xl font-black text-cream mb-4 uppercase tracking-wider">
              Thank You!
            </h1>
            
            <p className="text-xl text-warmGray mb-2">
              Your order has been confirmed
            </p>
            
            <p className="text-warmGray">
              You'll receive a confirmation email shortly with your order details and tracking information.
            </p>
          </div>

          {/* Divider */}
          <div className="border-t border-warmSage/30 my-12"></div>

          {/* What's Next */}
          <div className="bg-warmSage/10 rounded-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-cream mb-4 uppercase">What's Next?</h2>
            <div className="space-y-4 text-warmGray">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-warmSage flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-charcoal font-bold text-sm">1</span>
                </div>
                <div>
                  <p className="font-bold text-cream">Check Your Email</p>
                  <p className="text-sm">You'll receive an order confirmation with all the details.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-warmSage flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-charcoal font-bold text-sm">2</span>
                </div>
                <div>
                  <p className="font-bold text-cream">We'll Prepare Your Order</p>
                  <p className="text-sm">Our team will carefully prepare your items for shipment.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-warmSage flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-charcoal font-bold text-sm">3</span>
                </div>
                <div>
                  <p className="font-bold text-cream">Track Your Package</p>
                  <p className="text-sm">Once shipped, you'll receive tracking information to follow your order.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Brand Message with Family Photo */}
          <div className="text-center mb-12">
            <h3 className="text-2xl font-black text-cream mb-6 uppercase">
              From The PRSVR Family
            </h3>
            
            {/* Family Photo */}
            <div className="mb-8 rounded-lg overflow-hidden max-w-2xl mx-auto shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_prsvr-store/artifacts/3fw9ox3x_IMG_8467.jpeg"
                alt="The PRSVR Family"
                className="w-full h-auto"
              />
            </div>
            
            <p className="text-warmGray text-lg mb-4 italic">
              "Thank you for your order and for being part of our story."
            </p>
            
            <p className="text-warmGray mb-6">
              We're more than a brand – we're a family-owned business and a movement of those who persevere. 
              From Chicago to LA and everywhere in between. Built to Keep Going.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="grid md:grid-cols-2 gap-4 mb-12">
            <Button 
              onClick={() => navigate('/shop')}
              className="w-full bg-warmSage text-charcoal hover:bg-warmSage/90 font-black text-lg py-6 uppercase tracking-wider"
            >
              <ShoppingBag className="mr-2 h-5 w-5" />
              Continue Shopping
            </Button>
            
            <Button 
              onClick={() => navigate('/')}
              variant="outline"
              className="w-full border-2 border-warmSage text-warmSage hover:bg-warmSage hover:text-charcoal font-black text-lg py-6 uppercase tracking-wider"
            >
              Back to Home
            </Button>
          </div>

          {/* Social Follow */}
          <div className="text-center border-t border-warmSage/30 pt-8">
            <h3 className="text-xl font-bold text-cream mb-4 uppercase">Stay Connected</h3>
            <p className="text-warmGray mb-6">
              Follow us for exclusive drops, behind-the-scenes content, and the PRSVR lifestyle.
            </p>
            
            <div className="flex justify-center gap-4">
              <a 
                href="https://www.instagram.com/prsvr" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-warmSage/20 hover:bg-warmSage flex items-center justify-center transition-colors group"
              >
                <Instagram className="w-6 h-6 text-warmSage group-hover:text-charcoal" />
              </a>
              
              <a 
                href="https://www.youtube.com/@prsvr" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-warmSage/20 hover:bg-warmSage flex items-center justify-center transition-colors group"
              >
                <Youtube className="w-6 h-6 text-warmSage group-hover:text-charcoal" />
              </a>
            </div>
          </div>

          {/* Support */}
          <div className="text-center mt-12 text-sm text-warmGray">
            <p>Questions about your order?</p>
            <a href="mailto:info@prsvr.com" className="text-warmSage hover:text-cream underline">
              Contact us at info@prsvr.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThankYou;
